import { useApolloClient } from '@lessonup/client-integration';
import {
  Button,
  Divider,
  IconFolder,
  IconMicrophone,
  IconUpload,
  rem,
  SpaceBetween,
  spacing,
  styled,
  TextField,
  useErrorContext,
  useForm,
} from '@lessonup/ui-components';
import { asError, isValidUrl } from '@lessonup/utils';
import { isNull } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { UploadDetailsDocument } from '../../../../Uploads/Uploads.graphql.generated';
import { showUploadsModal } from '../../../../Uploads/UploadsModalFeature';
import { showAudioUploadModal } from '../../../../Uploads/components/AudioUpload/AudioUploadModal';
import { UploadProgress } from '../../../../Uploads/components/UploadProgress/UploadProgress';
import { useUploadFile } from '../../../../Uploads/hooks/useUploadFile';
import { AudioPickerModalSharedProps, HandleAddAudio } from './AudioPickerModal.types';

interface AudioPickerModalContentProps extends AudioPickerModalSharedProps {
  handleAddAudio: HandleAddAudio;
  closeModal: () => void;
  onUploadStateChange?: (isUploading: boolean) => void;
}

interface AddAudioFromLinkFormInput {
  url: string;
}

export const AudioPickerModalContent: React.FC<AudioPickerModalContentProps> = ({
  handleAddAudio,
  closeModal,
  t,
  onUploadStateChange,
}) => {
  const {
    register,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm<AddAudioFromLinkFormInput>();
  const urlInput = watch('url');

  const [uploadInfo, setUploadInfo] = useState<{ id: string } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setError } = useErrorContext();
  const client = useApolloClient();
  const [performUpload] = useUploadFile({ onError: (error: Error) => setError({ error }) });

  // Notify parent component when upload state changes
  useEffect(() => {
    onUploadStateChange?.(isUploading);
  }, [isUploading, onUploadStateChange]);

  const handleUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const uploadId = await performUpload({ file });

      if (!uploadId) {
        throw new Error('Upload unsuccessful.');
      }

      setUploadInfo({ id: uploadId });
    } catch (error) {
      setError({ error: asError(error) });
      setIsUploading(false);
    }
  };

  const handleAfterUpload = async () => {
    if (isNull(uploadInfo)) {
      closeModal();
      return;
    }

    try {
      const uploadViewer = await client.query({
        query: UploadDetailsDocument,
        variables: { uploadId: uploadInfo.id },
      });

      // We always only expect one upload.
      const upload = uploadViewer.data.viewer?.uploadsByIds?.[0];
      if (upload?.__typename === 'AudioUpload' && upload.audio?.url) {
        handleAddAudio({ url: upload.audio.url, source: 'upload' });
      }
      closeModal();
    } catch (error) {
      setError({ error: asError(error) });
    }
  };

  // Not a true cancel, the background upload will continue
  const handleCancelUpload = () => {
    setIsUploading(false);
    setUploadInfo(null);
    closeModal();
  };

  const handleBrowseUploads = async () => {
    const result = await showUploadsModal({ fileTypeFilter: ['AudioUpload'] });
    if (result?.urls && result.urls.length > 0) {
      handleAddAudio({ url: result.urls[0], source: 'myUploads' });
      closeModal();
    }
  };

  const onSubmitUrl = (input: AddAudioFromLinkFormInput) => {
    handleAddAudio({ url: input.url.trim(), source: 'pasteUrl' });
    closeModal();
  };

  const handleVoiceRecord = async () => {
    await showAudioUploadModal((upload) => {
      handleAddAudio({ url: upload.url, source: 'upload' });
      closeModal();
    });
  };

  return (
    <SpaceBetween direction="y" spacing={spacing.size16} style={{ padding: spacing.size24, width: rem('400px') }}>
      {!isUploading && !uploadInfo && (
        <>
          <Button onClick={() => fileInputRef.current?.click()} iconStart={<IconUpload />} buttonType="secondary">
            {t('audioPicker.uploadAudio')}
          </Button>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUploadFile}
            accept="audio/*"
            style={{ display: 'none' }}
          />

          <Button onClick={handleBrowseUploads} iconStart={<IconFolder />} buttonType="neutral">
            {t('audioPicker.openUploads')}
          </Button>

          <Button onClick={handleVoiceRecord} iconStart={<IconMicrophone />} buttonType="tertiary">
            {t('audioPicker.recordAudio')}
          </Button>

          <Divider orientation="horizontal" text={t('audioPicker.or')} />

          <form onSubmit={handleSubmit(onSubmitUrl)}>
            <StyledTextField
              {...register('url', {
                validate: (value: string) => {
                  if (!isValidUrl(value)) {
                    return t('audioPicker.urlInvalid');
                  }
                  return true;
                },
              })}
              type="url"
              size="small"
              placeholder={t('audioPicker.urlInputPlaceholder')}
              label={t('audioPicker.linkLabel')}
              autoComplete="off"
              validation={
                errors.url
                  ? {
                      type: 'negative',
                      message: errors.url.message as string,
                    }
                  : undefined
              }
              endSlot={
                <Button
                  size="small"
                  buttonType="neutral"
                  showStroke
                  type="submit"
                  buttonShape="rect"
                  disabled={!urlInput}
                >
                  {t('audioPicker.addButton')}
                </Button>
              }
            />
          </form>
        </>
      )}

      {isUploading && uploadInfo && (
        <SpaceBetween direction="y" spacing={spacing.size16} alignItems="center" style={{ width: '100%' }}>
          <UploadProgress
            uploadId={uploadInfo.id}
            onCompleted={handleAfterUpload}
            onError={(error: Error) => setError({ error })}
          />
          <Button onClick={handleCancelUpload} buttonType="secondary">
            {t('audioPicker.cancelUpload')}
          </Button>
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
};

const StyledTextField = styled(TextField)`
  width: 100%;
  max-width: ${rem('400px')};
`;
